import { compareAsc, compareDesc, isValid } from 'date-fns';
import type { ChartResponse } from '../../../redux/config/chart-data';
import type { WidgetConfigurationData } from '../../../redux/config/widgetApi';
import type { CustomColorProps } from '../../WidgetSettingWindow/components/config-components.interfaces';
import { dateStringFormatter } from './formate-date-fields';

export const SortDirection = {
  Ascending: 0,
  Descending: 1,
} as const;
export type SortDirection = (typeof SortDirection)[keyof typeof SortDirection];

export function intervalCounter<T>(transformedData: T[]) {
  if (transformedData.length > 130) {
    return 3;
  } else if (transformedData.length > 90) {
    return 2;
  } else if (transformedData.length > 65) {
    return 1;
  } else {
    return 0;
  }
}

// Font size constants for Y-axis labels
const Y_AXIS_FONT_SIZES = {
  VERY_SMALL: 4,
  SMALL: 6,
  MEDIUM: 8,
  LARGE: 12,
  DEFAULT: 16,
} as const;

// Data length and label length thresholds
const Y_AXIS_THRESHOLDS = {
  MAX_DATA_LENGTH_FOR_SMALL_FONT: 25,
  VERY_LONG_LABEL: 60,
  LONG_LABEL: 40,
  MEDIUM_LABEL: 25,
  SHORT_LABEL: 10,
} as const;

export function calculateYAxisFontSize(transformedData: ChartResponse[] | undefined): number {
  if (transformedData) {
    if (transformedData.length >= Y_AXIS_THRESHOLDS.MAX_DATA_LENGTH_FOR_SMALL_FONT) {
      return Y_AXIS_FONT_SIZES.MEDIUM;
    }
    const longestLabelLength = Math.max(...transformedData.map((el) => el.field?.length ?? 0));
    switch (true) {
      case longestLabelLength > Y_AXIS_THRESHOLDS.VERY_LONG_LABEL:
        return Y_AXIS_FONT_SIZES.VERY_SMALL;
      case longestLabelLength > Y_AXIS_THRESHOLDS.LONG_LABEL:
        return Y_AXIS_FONT_SIZES.SMALL;
      case longestLabelLength > Y_AXIS_THRESHOLDS.MEDIUM_LABEL:
        return Y_AXIS_FONT_SIZES.MEDIUM;
      case longestLabelLength > Y_AXIS_THRESHOLDS.SHORT_LABEL:
        return Y_AXIS_FONT_SIZES.LARGE;
      default:
        return Y_AXIS_FONT_SIZES.DEFAULT;
    }
  }
  return Y_AXIS_FONT_SIZES.DEFAULT;
}

// Generic sorter that supports string, number, or date-like values. Avoids explicit any.

export function genericSort<T, K extends keyof T>(
  data: T[],
  sortKey: K,
  direction: SortDirection = SortDirection.Ascending
): T[] {
  return data.sort((a, b) => {
    const av = a[sortKey] as T[K];
    const bv = b[sortKey] as T[K];

    // Normalize undefined/null to sortable baseline
    if (av == null && bv == null) return 0;
    if (av == null) return direction === SortDirection.Ascending ? -1 : 1;
    if (bv == null) return direction === SortDirection.Ascending ? 1 : -1;

    // Date detection (string convertible or Date object); avoid constructing invalid Dates for clearly non-date types.
    const maybeParseDate = (v: unknown): Date | null => {
      if (v instanceof Date) return isValid(v) ? v : null;
      if (typeof v === 'string') {
        const parsed = dateStringFormatter(v, true, true) as unknown; // unknown to allow safe refinement
        if (parsed instanceof Date) {
          return isValid(parsed) ? parsed : null;
        }
        const constructed = new Date(parsed as string | number);
        return isValid(constructed) ? constructed : null;
      }
      return null;
    };

    const aDate = maybeParseDate(av);
    const bDate = maybeParseDate(bv);
    if (aDate && bDate) {
      const cmp = direction === SortDirection.Ascending ? compareAsc(aDate, bDate) : compareDesc(aDate, bDate);
      if (!isNaN(cmp)) return cmp;
    }

    // String compare if both strings
    if (typeof av === 'string' && typeof bv === 'string') {
      const cmp = av.localeCompare(bv);
      return direction === SortDirection.Ascending ? cmp : -cmp;
    }

    // Numeric compare if both numbers
    if (typeof av === 'number' && typeof bv === 'number') {
      return direction === SortDirection.Ascending ? av - bv : bv - av;
    }

    // Fallback: convert to string (preserves previous behavior without unsafe casts)
    const aStr = String(av);
    const bStr = String(bv);
    const cmp = aStr.localeCompare(bStr);
    return direction === SortDirection.Ascending ? cmp : -cmp;
  });
}
/**
 * A helper method to indicate if a legend is to be shown for a chart.
 * Missing `displayLegend` attribute must be treated as `true`.
 *
 * @param configurationData of a widget which legend visibility is to be defined
 * @returns boolean
 */
export const includeLegend = (configurationData: WidgetConfigurationData | undefined): boolean => {
  const widgetData = configurationData?.widgetData;

  if (widgetData?.secondField) {
    if (widgetData?.displayLegend !== undefined) {
      // If the widget has a secondary field and displayLegend property exists then use
      // Legend visibility explicitly set by the user
      return widgetData?.displayLegend;
    } else {
      // If displayLegend property does not exists, set to true
      return true;
    }
  } else {
    // If the widget has no secondary field, set to false
    return false;
  }
};

export const findCustomColor = (label: string | undefined, customColors: Array<CustomColorProps>) => {
  if (customColors && label) {
    return customColors.find((item) => item.label === label)?.color;
  }
};
